import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { ShoppingCart, Search, X, Loader2, User, LogOut } from 'lucide-react';
import './Navbar.css';
import { AuthContext } from '../App';



const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setLoading(true);

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/products?search=${encodeURIComponent(searchQuery.trim())}`
        );

        if (!response.ok) {
          throw new Error('Search failed');
        }

        const data = await response.json();
        setResults(data.slice(0, 8));
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, []);

  const handleProductSelect = (product) => {
    if (product?.slug || product?._id) {
      navigate(`/product/${product.slug || product._id}`);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="search-modal-overlay">
      <div className="search-modal" ref={searchRef}>
        <div className="search-modal-header">
          <div className="search-input-wrapper">
            <Search className="search-icon" size={20} />
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder="Wyszukaj produkt..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                handleSearch(e.target.value);
              }}
              autoComplete="off"
            />
            {query && (
              <button
                className="clear-button"
                onClick={() => {
                  setQuery('');
                  setResults([]);
                }}
                aria-label="Wyczyść wyszukiwanie"
              >
                <X size={18} />
              </button>
            )}
          </div>
          <button
            className="close-button"
            onClick={onClose}
            aria-label="Zamknij wyszukiwanie"
          >
            <X size={24} />
          </button>
        </div>

        {(query || loading || results.length > 0) && (
          <div className="search-results-container">
            {loading ? (
              <div className="search-loading">
                <Loader2 className="spin" size={24} />
                <span>Wyszukiwanie...</span>
              </div>
            ) : query.trim() && results.length === 0 ? (
              <div className="no-results">
                <p>Brak wyników dla "{query}"</p>
              </div>
            ) : results.length > 0 ? (
              <div className="search-results">
                {results.map((product, index) => (
                  <button
                    key={product._id}
                    className={`search-result ${index === selectedIndex ? 'selected' : ''}`}
                    onClick={() => handleProductSelect(product)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="product-image">
                      <img
                        src={product.imageUrls?.[0] || '/ikona.png'}
                        alt={product.displayName}
                        onError={(e) => { e.target.src = '/ikona.png' }}
                      />
                    </div>
                    <div className="product-info">
                      <span className="product-name">
                        {product.displayName}
                      </span>
                      <span className="product-price">
                        {typeof product.price === 'number' ? product.price.toFixed(2) : '0.00'} zł
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

const Navbar = React.memo(() => {
  const { isAuthenticated, logout } = useContext(AuthContext);
  const [cartItemsCount, setCartItemsCount] = useState(0);
  const [showSearch, setShowSearch] = useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleCartClick = () => {
    navigate('/checkout');
  };

  useEffect(() => {
    const fetchCartItems = async () => {
      try {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        
        if (!token || !userId) {
          setCartItemsCount(0);
          return;
        }
        
        const response = await fetch(`${API_BASE_URL}/api/cart/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const cart = await response.json();
          setCartItemsCount(cart.items.reduce((acc, item) => acc + item.quantity, 0));
        } else {
          setCartItemsCount(0);
        }
      } catch (error) {
        console.error('Error fetching cart items:', error);
        setCartItemsCount(0);
      }
    };

    fetchCartItems();

    // Listen for cart updates
    const handleCartUpdate = (event) => {
      const cart = event.detail;
      if (cart && cart.items) {
        setCartItemsCount(cart.items.reduce((acc, item) => acc + item.quantity, 0));
      } else {
        setCartItemsCount(0);
      }
    };

    window.addEventListener('cartUpdate', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cartUpdate', handleCartUpdate);
    };
  }, [isAuthenticated]);

  return (
    <>
      <nav className="navbar">
        <div className="navbar-left">
          <Link to="/" className="brand-logo">
            <span className="brand-text">OUTLET ZA TORAMI</span>
          </Link>
        </div>
        <div className="navbar-middle">
          <div className="search-container" onClick={() => setShowSearch(true)}>
            <input type="text" className="search-input" placeholder="Szukaj w OUTLET ZA TORAMI" readOnly />
            <Search className="search-icon" />
          </div>
        </div>
        <div className="navbar-right">
          {isAuthenticated ? (
            <>
              <Link to="/profile" className="user-action">
                <User className="icon" />
                <span>Profil</span>
              </Link>
              <Link to="#" onClick={handleLogout} className="user-action">
                <LogOut className="icon" />
                <span>Wyloguj</span>
              </Link>
            </>
          ) : (
            <Link to="/login" className="user-action">
              <User className="icon" />
              <span>Zaloguj</span>
            </Link>
          )}
          <div className="cart-action user-action" onClick={handleCartClick}>
            <ShoppingCart className="icon" />
            <span>Koszyk</span>
            {cartItemsCount > 0 && (
              <div className="cart-badge">{cartItemsCount}</div>
            )}
          </div>
        </div>
      </nav>
      <SearchModal isOpen={showSearch} onClose={() => setShowSearch(false)} />
    </>
  );
});

export default Navbar;